import type { ReportWidgetInput } from "@erp/shared-types";
import type { CashQueryInput } from "./ar-cash-dashboard.grammar";
import { CASH_QUERY_CATALOG } from "./ar-cash-dashboard.source";

/**
 * The Cash Dashboard's template: the default layout a user starts from,
 * reproducing the static cash metrics page (`/accounts-receivable/cash-metrics`)
 * widget for widget, with its titles, descriptions, notes and tooltips. Its
 * widgets are the report canvas's own (KPI, Table, custom Vega chart), each
 * bound to a cash query through its `source`; the page runs the queries and
 * hands each widget its answer as a dataset. Charts are Vega-Lite specs over
 * those rows.
 *
 * Read by the page (the layout a user without a saved one sees) and by
 * Nami's dashboard tools (reset, "add the new widgets"). The text is written
 * in English, as a saved layout keeps its widgets' text as data.
 *
 * Changing the template: bump `version`, and give each widget it adds
 * `since: <new version>`, so saved layouts on an older version show the
 * update banner and know which widgets are new.
 */

/** The persisted-layouts key of a dashboard. Never rename one: rows reference it. */
export function dashboardSurfaceId(dashboardId: string): string {
  return `ar-dashboard:${dashboardId}`;
}

export const CASH_DASHBOARD_ID = "cash";
export const CASH_DASHBOARD_SURFACE_ID = dashboardSurfaceId(CASH_DASHBOARD_ID);

/** One widget of a template, at its place on the 12-column grid. */
export interface DashboardTemplateItem {
  x: number;
  y: number;
  w: number;
  h: number;
  component: ReportWidgetInput;
  /** The template version that introduced the widget; absent = 1. */
  since?: number;
}

export interface DashboardTemplate {
  id: string;
  version: number;
  items: readonly DashboardTemplateItem[];
}

/** True when a layout stamped with `version` predates `template`. Unstamped counts as older. */
export function isTemplateVersionOutdated(
  version: number | null | undefined,
  template: DashboardTemplate,
): boolean {
  return (version ?? 0) < template.version;
}

/**
 * Template widgets introduced after `version` that the layout (its widget
 * ids) lacks: what "add the new widgets" adds.
 */
export function newTemplateItems(
  template: DashboardTemplate,
  version: number,
  presentIds: Iterable<string>,
): DashboardTemplateItem[] {
  const present = new Set(presentIds);
  return template.items.filter(
    (item) => (item.since ?? 1) > version && !present.has(item.component.id),
  );
}

// ── Removing and re-adding widgets ──────────────────────────────────────────
// The grid packs widgets upward; these keep a row closed up sideways too,
// so a row of KPI tiles with one removed has no hole. Pure, on the saved
// layout's items, so the page and Nami's tools edit layouts the same way.

const GRID_COLUMNS = 12;

interface PlacedWidget {
  x: number;
  y: number;
  w: number;
  h: number;
  component: { id: string };
}

function overlaps(a: Omit<PlacedWidget, "component">, b: PlacedWidget) {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  );
}

/**
 * `layout` without widget `id`, its row closed up: each widget to its right
 * that starts on the same row moves left by the removed widget's width,
 * nearest first, where nothing else is in the way. Unknown id: unchanged.
 */
export function removeLayoutWidget<
  W extends PlacedWidget,
  L extends { items: readonly W[] },
>(layout: L, id: string): L {
  const removed = layout.items.find((item) => item.component.id === id);
  if (!removed) return layout;
  const items: W[] = layout.items.filter((item) => item !== removed);
  const peers = items
    .filter((item) => item.y === removed.y && item.x >= removed.x + removed.w)
    .sort((a, b) => a.x - b.x);
  for (const peer of peers) {
    const moved = { ...peer, x: peer.x - removed.w };
    const others = items.filter((item) => item !== peer);
    if (others.some((other) => overlaps(moved, other))) continue;
    items[items.indexOf(peer)] = moved;
  }
  return { ...layout, items };
}

/**
 * The first place a `w`×`h` widget fits on the grid without covering
 * another: rows top to bottom, then columns left to right; below everything
 * when nothing above is free.
 */
export function firstFreeSlot(
  items: readonly PlacedWidget[],
  size: { w: number; h: number },
): { x: number; y: number } {
  const w = Math.min(GRID_COLUMNS, size.w);
  const bottom = items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  for (let y = 0; y <= bottom; y++) {
    for (let x = 0; x + w <= GRID_COLUMNS; x++) {
      const spot = { x, y, w, h: size.h };
      if (!items.some((item) => overlaps(spot, item))) return { x, y };
    }
  }
  return { x: 0, y: bottom };
}

/** `layout` with `widget` added in the first free slot it fits. */
export function addLayoutWidget<
  W extends PlacedWidget,
  L extends { items: readonly W[] },
>(layout: L, widget: W): L {
  const slot = firstFreeSlot(layout.items, widget);
  return { ...layout, items: [...layout.items, { ...widget, ...slot }] };
}

// ── Vega-Lite helpers ──────────────────────────────────────────────────────

type Spec = Record<string, unknown>;

/** A Vega expression mapping `field`'s values to labels, in order. */
function labelExpr(field: string, labels: readonly [string | null, string][]) {
  return labels.reduceRight(
    (rest, [value, label]) =>
      `datum[${JSON.stringify(field)}] === ${JSON.stringify(value)} ? ${JSON.stringify(label)} : ${rest}`,
    JSON.stringify("Other"),
  );
}

export interface BreakdownOptions {
  /** The category field. */
  category: string;
  /**
   * Its values in display order, with labels. Absent: the rows ranked by
   * `value`, largest first, labelled by `labelField`, at most `rows` of them.
   */
  categories?: readonly [string | null, string][];
  /** Ranked bars: the field holding each row's label (default `category`). */
  labelField?: string;
  /** Ranked bars: how many rows to draw. */
  rows?: number;
  /** Values drawn in the full series color; the rest are a lighter tint. */
  emphasis?: readonly string[];
  /** What the bar's length encodes. */
  value: string;
  /** Count and, optionally, dollars shown beside the bar. */
  count?: string;
  amount?: string;
  /** The value label's d3 format when there is no count (default ",d"). */
  valueFormat?: string;
  /** Each row's share is of this field (per row); absent = of the rows' sum. */
  totalField?: string;
  /** Rows arrive as one row of measures; fold them into categories first. */
  fold?: boolean;
  /** Leave out each row's share: for values that don't add up (rates, days). */
  noShare?: boolean;
  /** Tooltip title of the count (or of the value, without a count). */
  countTitle: string;
}

/**
 * Horizontal bars in a fixed category order, as the static page's breakdown
 * bars: a label, a bar scaled to the largest row, and a direct value label
 * ("12 · $304,079.89  41%") with the row's share.
 */
export function breakdownBars(options: BreakdownOptions): Spec {
  const ranked = options.categories === undefined;
  const order = (options.categories ?? []).map(([value]) => value);
  const labelField = JSON.stringify(options.labelField ?? options.category);
  const amountText = options.amount
    ? ` + " · " + format(toNumber(datum[${JSON.stringify(options.amount)}]), "$,.2f")`
    : "";
  const transform: Spec[] = [];
  if (options.fold && !ranked) {
    transform.push(
      { fold: order, as: [options.category, options.count] },
      {
        calculate: `datum[${JSON.stringify(options.count)}]`,
        as: options.value,
      },
    );
  }
  if (ranked) {
    // Largest first; a null label reads as "Not recorded".
    transform.push(
      {
        calculate: `toNumber(datum[${JSON.stringify(options.value)}])`,
        as: "length",
      },
      {
        window: [{ op: "row_number", as: "rank" }],
        sort: [{ field: "length", order: "descending" }],
      },
      { filter: `datum.rank <= ${options.rows ?? 8}` },
      { calculate: "datum.rank - 1", as: "order" },
      {
        calculate: `datum[${labelField}] == null ? "Not recorded" : datum[${labelField}]`,
        as: "label",
      },
    );
  } else {
    transform.push(
      {
        filter: `indexof(${JSON.stringify(order)}, datum[${JSON.stringify(options.category)}]) >= 0`,
      },
      {
        calculate: labelExpr(options.category, options.categories ?? []),
        as: "label",
      },
      {
        calculate: `indexof(${JSON.stringify(order)}, datum[${JSON.stringify(options.category)}])`,
        as: "order",
      },
    );
  }
  transform.push(
    {
      calculate: `indexof(${JSON.stringify(options.emphasis ?? [])}, datum[${JSON.stringify(options.category)}]) >= 0`,
      as: "emphasis",
    },
    {
      calculate: `toNumber(datum[${JSON.stringify(options.value)}])`,
      as: "length",
    },
    options.totalField
      ? {
          calculate: `datum[${JSON.stringify(options.totalField)}]`,
          as: "denominator",
        }
      : { joinaggregate: [{ op: "sum", field: "length", as: "denominator" }] },
    {
      calculate:
        "datum.denominator > 0 ? datum.length / datum.denominator : null",
      as: "share",
    },
    {
      calculate: options.count
        ? `format(datum[${JSON.stringify(options.count)}], ",d")`
        : `format(datum.length, ${JSON.stringify(options.valueFormat ?? ",d")})`,
      as: "countText",
    },
    {
      calculate: `datum.countText${amountText}`,
      as: "valueText",
    },
    {
      calculate: options.noShare
        ? '""'
        : 'datum.share == null ? "" : format(datum.share, ".0%")',
      as: "shareText",
    },
  );
  const tooltip: Spec[] = [
    { field: "label", title: "Category" },
    options.count
      ? { field: options.count, title: options.countTitle, format: ",d" }
      : {
          field: "length",
          title: options.countTitle,
          format: options.valueFormat ?? ",d",
        },
  ];
  if (options.amount)
    tooltip.push({
      field: options.amount,
      title: "Dollars",
      type: "quantitative",
      format: "$,.2f",
    });
  if (!options.noShare)
    tooltip.push({ field: "share", title: "Share", format: ".0%" });
  const rows = options.categories?.length ?? options.rows ?? 8;
  // A continuous row axis rather than a band scale: a band scale sizes the
  // chart by step and ignores the cell's height.
  const y = {
    field: "order",
    type: "quantitative",
    axis: null,
    scale: {
      domain: [-0.5, rows - 0.5],
      reverse: true,
      nice: false,
      zero: false,
    },
  };
  const text = { type: "text", baseline: "middle", fontSize: 12 };
  // Room on the right for the value and share labels. A narrow chart (a
  // phone) drops the dollars from the value label to keep its bars.
  const narrow = "width < 420";
  const barEnd = options.amount
    ? `${narrow} ? width - 90 : width - 190`
    : "width - 90";
  return {
    transform,
    layer: [
      {
        mark: {
          ...text,
          align: "left",
          limit: { expr: "width * 0.4 - 8" },
          opacity: 0.7,
        },
        encoding: { x: { value: 0 }, y, text: { field: "label" }, tooltip },
      },
      {
        mark: {
          type: "bar",
          orient: "horizontal",
          size: 10,
          cornerRadius: 3,
          color: "#8882",
        },
        encoding: {
          x: { value: { expr: "width * 0.4" } },
          x2: { value: { expr: barEnd } },
          y,
        },
      },
      {
        mark: { type: "bar", orient: "horizontal", size: 10, cornerRadius: 3 },
        encoding: {
          x: {
            field: "length",
            type: "quantitative",
            axis: null,
            scale: {
              domainMin: 0,
              range: [{ expr: "width * 0.4" }, { expr: barEnd }],
            },
          },
          x2: { datum: 0 },
          y,
          opacity: {
            condition: { test: "datum.emphasis", value: 1 },
            value: 0.6,
          },
          tooltip,
        },
      },
      {
        mark: { ...text, align: "right" },
        encoding: {
          x: { value: { expr: "width - 44" } },
          y,
          text: options.amount
            ? {
                condition: { test: narrow, field: "countText" },
                field: "valueText",
              }
            : { field: "valueText" },
          tooltip,
        },
      },
      {
        mark: { ...text, align: "right", opacity: 0.6, fontSize: 11 },
        encoding: {
          x: { value: { expr: "width" } },
          y,
          text: { field: "shareText" },
          tooltip,
        },
      },
    ],
  };
}

/** A breakdown row's height: a line of text and the gap under it, as the static page. */
const BREAKDOWN_ROW_HEIGHT = 26;

/** The chart's own padding, top and bottom, which its height includes. */
const BREAKDOWN_PADDING = 26;

/** The natural plot height of a breakdown of `rows` categories. */
export function breakdownHeight(rows: number): number {
  return rows * BREAKDOWN_ROW_HEIGHT + BREAKDOWN_PADDING;
}

const KEYED_BY: [string, string][] = [
  ["gently", "Keyed by Gently"],
  ["team", "Keyed by the team"],
];

/**
 * Each day's receipts as a 100% stacked bar by who keyed them, with the day's
 * counts and shares in its tooltip. A day with no receipts keeps its slot,
 * drawn as a dashed outline.
 */
function dailyFilingSpec(): Spec {
  const day = 'utcFormat(toDate(datum.day), "%a, %b %-d")';
  const tooltip = [
    { field: "dayLabel", title: "Day" },
    { field: "total", title: "Receipts", format: ",d" },
    { field: "gentlyCount", title: "Keyed by Gently", format: ",d" },
    { field: "gentlyShare", title: "Keyed by Gently, share", format: ".0%" },
    { field: "teamCount", title: "Keyed by the team", format: ",d" },
    { field: "teamShare", title: "Keyed by the team, share", format: ".0%" },
  ];
  return {
    transform: [
      {
        pivot: "keyed_by",
        value: "receipt_count",
        groupby: ["day"],
        op: "sum",
      },
      { calculate: "datum.gently || 0", as: "gentlyCount" },
      { calculate: "datum.team || 0", as: "teamCount" },
      { calculate: "datum.gentlyCount + datum.teamCount", as: "total" },
      {
        calculate: "datum.total > 0 ? datum.gentlyCount / datum.total : null",
        as: "gentlyShare",
      },
      {
        calculate: "datum.total > 0 ? datum.teamCount / datum.total : null",
        as: "teamShare",
      },
      { calculate: day, as: "dayLabel" },
      { fold: ["gentlyCount", "teamCount"], as: ["series", "count"] },
      {
        calculate:
          'datum.series === "gentlyCount" ? "Keyed by Gently" : "Keyed by the team"',
        as: "seriesLabel",
      },
      {
        calculate: 'datum.series === "gentlyCount" ? 0 : 1',
        as: "stackOrder",
      },
    ],
    encoding: {
      // A time scale keeps a slot for every day, so days with no receipts
      // read as gaps rather than closing the axis up.
      x: {
        field: "day",
        type: "temporal",
        timeUnit: "utcyearmonthdate",
        axis: {
          title: null,
          labelExpr: 'utcFormat(datum.value, "%-d")',
          labelAngle: 0,
          ticks: false,
          domain: false,
          grid: false,
        },
      },
    },
    layer: [
      {
        mark: { type: "bar", cornerRadiusEnd: 2 },
        encoding: {
          y: {
            field: "count",
            type: "quantitative",
            stack: "normalize",
            axis: {
              title: null,
              format: ".0%",
              values: [0, 0.5, 1],
              grid: true,
              domain: false,
              ticks: false,
            },
          },
          // One hue, as the static page: Gently in the full series color,
          // the team as a light tint of it.
          opacity: {
            field: "seriesLabel",
            type: "nominal",
            scale: {
              domain: KEYED_BY.map(([, label]) => label),
              range: [1, 0.3],
            },
            legend: { orient: "top", title: null, symbolType: "square" },
          },
          order: { field: "stackOrder" },
          tooltip,
        },
      },
      {
        transform: [
          { filter: 'datum.total === 0 && datum.series === "gentlyCount"' },
        ],
        mark: {
          type: "bar",
          filled: false,
          strokeDash: [3, 3],
          strokeOpacity: 0.5,
          cornerRadius: 2,
        },
        encoding: {
          y: { datum: 0, type: "quantitative" },
          y2: { datum: 1 },
          tooltip: [
            { field: "dayLabel", title: "Day" },
            { field: "total", title: "Receipts", format: ",d" },
          ],
        },
      },
    ],
  };
}

// ── Template ──────────────────────────────────────────────────────────────

const KPI_H = 3;

/** A widget's source: a query in the cash catalog. */
function cash(query: CashQueryInput) {
  return { catalog: CASH_QUERY_CATALOG, query };
}

function kpi(
  x: number,
  component: Omit<Extract<ReportWidgetInput, { type: "kpi" }>, "type">,
): DashboardTemplateItem {
  return {
    x,
    y: 0,
    w: 2,
    h: KPI_H,
    component: { type: "kpi", ...component },
  };
}

const PAYMENT_TYPES: [string, string][] = [
  ["check", "Check"],
  ["electronic", "ACH / wire"],
  ["cash", "Cash"],
  ["other", "Other"],
];

const HALF = 6;

export const CASH_DASHBOARD_TEMPLATE: DashboardTemplate = {
  id: CASH_DASHBOARD_ID,
  // 2: the widgets became the report canvas's own types, bound by `source`.
  version: 2,
  items: [
    kpi(0, {
      id: "cash-kpi-paid-by-ach",
      title: "Paid by ACH",
      tooltip:
        "Share of check and electronic receipt dollars that came in electronically. Famous records ACH and wire under one code, so wires are included.",
      source: cash({
        kind: "aggregate",
        measures: [
          "electronic_dollar_share",
          "customers_paying_electronically",
        ],
      }),
      value: "{electronic_dollar_share}",
      subtext:
        "{customers_paying_electronically} {customers_paying_electronically, plural, one {customer} other {customers}} paid by ACH or wire",
    }),
    kpi(2, {
      id: "cash-kpi-check-lag",
      title: "Check Lag",
      tooltip:
        "Median days from the date on a check to the day its receipt was entered in Famous, over the check receipts dated in the range.",
      source: cash({
        kind: "aggregate",
        measures: ["check_lag_median_days", "checks_with_check_date"],
      }),
      value:
        "{check_lag_median_days} {check_lag_median_days, plural, one {day} other {days}}",
      subtext:
        "Median over {checks_with_check_date} {checks_with_check_date, plural, one {check} other {checks}}",
    }),
    kpi(4, {
      id: "cash-kpi-filed-in-scope",
      title: "Filed by Gently, In Scope",
      tooltip:
        "Receipts Gently is set up to key: in a bank account and payment type it keyed any receipts for in the range (today Truist ACH), from customers whose remit type is Email. Of those, the share it keyed. Set a customer's remit type on their profile.",
      source: cash({
        kind: "aggregate",
        measures: [
          "in_scope_gently_keyed_share",
          "in_scope_gently_keyed_count",
          "in_scope_receipt_count",
        ],
      }),
      value: "{in_scope_gently_keyed_share}",
      subtext:
        "{in_scope_gently_keyed_count} of {in_scope_receipt_count} receipts from email remitters",
    }),
    kpi(6, {
      id: "cash-kpi-filed-by-gently",
      title: "Filed by Gently",
      tooltip:
        'Of the Famous cash receipts dated in the range, the share the Gently agent keyed: receipts whose description says "entry by Gently".',
      source: cash({
        kind: "aggregate",
        measures: ["gently_keyed_share", "gently_keyed_count", "receipt_count"],
      }),
      value: "{gently_keyed_share}",
      subtext: "{gently_keyed_count} of {receipt_count} receipts",
    }),
    kpi(8, {
      id: "cash-kpi-agent-success",
      title: "Agent Success Rate",
      tooltip:
        "Cash receipt tasks the agent worked on without escalating to a person.",
      source: cash({
        kind: "aggregate",
        measures: [
          "agent_success_rate",
          "agent_tasks_escalated",
          "agent_tasks_attempted",
        ],
      }),
      value: "{agent_success_rate}",
      subtext:
        "{agent_tasks_escalated} of {agent_tasks_attempted} tasks escalated",
    }),
    kpi(10, {
      id: "cash-kpi-email-remittances",
      title: "Email Remittances",
      tooltip:
        "Remittances Gently recorded from email, out of those plus the ones it missed.",
      source: cash({
        kind: "aggregate",
        measures: [
          "email_remittance_capture_rate",
          "remittance_count",
          "email_remittances_missed",
        ],
      }),
      value: "{email_remittance_capture_rate}",
      subtext: "{remittance_count} found, {email_remittances_missed} missed",
    }),

    // ── Left column: filing, email remittances (positions below) ──
    {
      x: 0,
      y: 3,
      w: HALF,
      h: 4,
      component: {
        type: "vega-chart",
        id: "cash-filing",
        title: "Cash Receipt Filing",
        description:
          'Famous cash receipts dated in the range, by who keyed them. The agent stamps every receipt it keys "entry by Gently" in the description; the rest were keyed by the team.',
        source: cash({
          kind: "aggregate",
          measures: ["receipt_count", "receipt_amount"],
          dimensions: ["keyed_by"],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          category: "keyed_by",
          categories: KEYED_BY,
          emphasis: ["gently"],
          value: "receipt_count",
          count: "receipt_count",
          amount: "receipt_amount",
          countTitle: "Receipts",
        }),
        plotHeight: breakdownHeight(2),
      },
    },
    {
      x: 0,
      y: 7,
      w: HALF,
      h: 7,
      component: {
        type: "table",
        id: "cash-filing-by-bank",
        title: "By bank account and payment type",
        source: cash({
          kind: "aggregate",
          measures: [
            "receipt_count",
            "gently_keyed_count",
            "gently_keyed_share",
            "email_remitter_gently_keyed_count",
            "email_remitter_receipt_count",
          ],
          dimensions: ["bank", "payment_type"],
        }),
        rowOrder: { group: "bank", by: "receipt_count" },
        columns: [
          {
            label: "Bank Account",
            key: "bank",
            nullText: "Not recorded in Famous",
          },
          {
            label: "Type",
            key: "payment_type",
            valueLabels: Object.fromEntries(PAYMENT_TYPES),
          },
          {
            label: "Receipts",
            key: "receipt_count",
            align: "right",
          },
          {
            label: "By Gently",
            key: "gently_keyed_count",
            align: "right",
          },
          {
            label: "Share",
            key: "gently_keyed_share",
            align: "right",
          },
          {
            label: "Email Remitters",
            text: "{email_remitter_gently_keyed_count} of {email_remitter_receipt_count}",
            align: "right",
          },
        ],
        emptyText: "No receipts in this range.",
      },
    },
    {
      x: 0,
      y: 14,
      w: HALF,
      h: 6,
      component: {
        type: "vega-chart",
        id: "cash-filing-daily",
        title: "Share of each day's receipts",
        source: cash({
          kind: "aggregate",
          measures: ["receipt_count"],
          dimensions: ["day", "keyed_by"],
        }),
        grammar: "vega-lite",
        spec: dailyFilingSpec(),
        link: {
          label: "Open cash receipts",
          href: "/accounts-receivable/cash-receipts",
        },
      },
    },
    {
      x: 6,
      y: 24,
      w: HALF,
      h: 5,
      component: {
        type: "vega-chart",
        id: "cash-remittance-bank",
        title: "Remittances vs. Bank",
        description:
          "Email remittances received in the range, by whether a bank receipt backs them. Ones dated after the newest bank receipt can't match yet. Matching needs the exact amount, so a linked remittance always agrees with the bank on dollars; the date is what drifts.",
        source: cash({
          kind: "aggregate",
          measures: ["remittance_count", "remittance_amount"],
          dimensions: ["remittance_status"],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          category: "remittance_status",
          categories: [
            ["linked", "Linked to a bank receipt"],
            ["unmatched", "Not matched to a bank receipt"],
            ["no_customer", "Not matched, no customer identified"],
            ["awaiting_bank", "Bank not caught up yet"],
          ],
          value: "remittance_count",
          count: "remittance_count",
          amount: "remittance_amount",
          countTitle: "Remittances",
        }),
        plotHeight: breakdownHeight(4),
      },
    },
    {
      x: 6,
      y: 29,
      w: HALF,
      h: 6,
      component: {
        type: "vega-chart",
        id: "cash-remittance-date-gap",
        title:
          "Remittance payment date vs. bank date (median {remittance_date_gap_median_days} {remittance_date_gap_median_days, plural, one {day} other {days}})",
        textSource: cash({
          kind: "aggregate",
          measures: ["remittance_date_gap_median_days"],
        }),
        source: cash({
          kind: "aggregate",
          measures: ["linked_remittance_count"],
          dimensions: ["date_gap_bucket"],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          category: "date_gap_bucket",
          categories: [
            ["same_day", "Same day"],
            ["1_3_days", "1–3 days"],
            ["4_7_days", "4–7 days"],
            ["8_14_days", "8–14 days"],
            ["15_31_days", "15–31 days"],
            ["over_31_days", "Over 31 days"],
            ["no_stated_date", "No date on remittance"],
          ],
          value: "linked_remittance_count",
          count: "linked_remittance_count",
          countTitle: "Remittances",
        }),
        plotHeight: breakdownHeight(7),
      },
    },
    {
      x: 0,
      y: 20,
      w: HALF,
      h: 5,
      component: {
        type: "vega-chart",
        id: "cash-email-remittances",
        title: "Email Remittances",
        description:
          "Remittances Gently recorded from email, and the ones it missed: emails it recognized as remittances but failed to record, cash receipts your team marked as missing their remittance, and remittances a person had to add by hand. Mark a receipt from its detail panel on the cash receipts page.",
        source: cash({
          kind: "aggregate",
          measures: [
            "remittance_count",
            "remittance_emails_failed",
            "marked_missing_count",
            "remittances_added_manually",
          ],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          fold: true,
          category: "status",
          categories: [
            ["remittance_count", "Found by Gently"],
            ["remittance_emails_failed", "Recognized but not recorded"],
            ["marked_missing_count", "Receipts marked missing a remittance"],
            ["remittances_added_manually", "Added by hand"],
          ],
          emphasis: ["remittance_count"],
          value: "count",
          count: "count",
          countTitle: "Remittances",
        }),
        plotHeight: breakdownHeight(4),
      },
    },
    {
      x: 0,
      y: 25,
      w: HALF,
      h: 4,
      component: {
        type: "table",
        id: "cash-failed-emails",
        title: "Latest emails recognized but not recorded",
        source: cash({
          kind: "list",
          list: "failed_remittance_emails",
          limit: 10,
        }),
        hideHeader: true,
        columns: [
          { label: "Received", key: "receivedAt", format: "date" },
          {
            label: "Subject",
            key: "subject",
            nullText: "(no subject)",
            href: "/inbound-emails?inboundEmailId={inboundEmailId}",
            secondary: { key: "sender" },
          },
        ],
      },
    },
    {
      x: 0,
      y: 29,
      w: HALF,
      h: 6,
      component: {
        type: "table",
        id: "cash-marked-missing",
        title: "Latest receipts marked missing a remittance",
        source: cash({
          kind: "list",
          list: "receipts_marked_missing",
          limit: 10,
        }),
        hideHeader: true,
        columns: [
          { label: "Receipt date", key: "transactionDate", format: "day" },
          {
            label: "Customer",
            key: "customerName",
            nullText: "Cash receipt",
            href: "/accounts-receivable/cash-receipts?receiptId={cashReceiptId}",
          },
          {
            label: "Amount",
            key: "amount",
            align: "right",
          },
        ],
      },
    },

    // ── Right column: the agent, check lag, remittances vs. bank ──
    {
      x: 6,
      y: 3,
      w: HALF,
      h: 4,
      component: {
        type: "vega-chart",
        id: "cash-agent",
        title: "Cash Receipt Agent",
        description:
          "Of the {agent_tasks_attempted} cash receipt tasks the agent worked on in the range. An escalation counts as a failure, since other failures aren't tracked yet.",
        source: cash({
          kind: "aggregate",
          measures: [
            "agent_tasks_keyed",
            "agent_tasks_escalated",
            "agent_tasks_attempted",
          ],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          fold: true,
          category: "result",
          categories: [
            ["agent_tasks_keyed", "Keyed by the agent"],
            ["agent_tasks_escalated", "Escalated to a person"],
          ],
          emphasis: ["agent_tasks_keyed"],
          value: "count",
          count: "count",
          totalField: "agent_tasks_attempted",
          countTitle: "Tasks",
        }),
        plotHeight: breakdownHeight(2),
      },
    },
    {
      x: 6,
      y: 7,
      w: HALF,
      h: 11,
      component: {
        type: "table",
        id: "cash-escalations",
        title: "Latest escalations, with the agent's question",
        source: cash({ kind: "list", list: "recent_escalations", limit: 10 }),
        hideHeader: true,
        columns: [
          { label: "Escalated", key: "escalatedAt", format: "date" },
          {
            label: "Customer",
            key: "customerName",
            nullText: "Cash receipt",
            href: "/accounts-receivable/cash-receipts?receiptId={cashReceiptId}",
            secondary: { key: "amount" },
            width: 14,
          },
          { label: "Question", key: "question", clamp: true },
        ],
      },
    },
    {
      x: 6,
      y: 18,
      w: HALF,
      h: 6,
      component: {
        type: "vega-chart",
        id: "cash-check-lag",
        title: "Check Lag",
        description:
          "Famous check receipts dated in the range, by days from the date on the check to the day its receipt was entered.",
        textSource: cash({
          kind: "aggregate",
          measures: [
            "check_lag_median_days",
            "check_lag_p90_days",
            "checks_dated_after_receipt",
            "check_count",
            "checks_with_check_date",
          ],
        }),
        notes: [
          {
            text: "Median {check_lag_median_days} days, 90% within {check_lag_p90_days} days",
            when: { value: "{checks_with_check_date}", is: "positive" },
            placement: "above",
          },
          {
            text: "No checks with a check date in this range.",
            when: { value: "{checks_with_check_date}", is: "zero" },
            placement: "above",
          },
          {
            text: "{checks_dated_after_receipt} {checks_dated_after_receipt, plural, one {check is} other {checks are}} dated after its receipt, probably a typo in Famous, and left out.",
            when: { value: "{checks_dated_after_receipt}", is: "positive" },
          },
          {
            text: "{check_count - checks_with_check_date} without a check date in Famous.",
            when: {
              value: "{check_count - checks_with_check_date}",
              is: "positive",
            },
          },
        ],
        source: cash({
          kind: "aggregate",
          measures: ["check_lag_count"],
          dimensions: ["check_lag_bucket"],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          category: "check_lag_bucket",
          categories: [
            ["same_day", "Same day"],
            ["1_3_days", "1–3 days"],
            ["4_7_days", "4–7 days"],
            ["8_14_days", "8–14 days"],
            ["over_14_days", "Over 14 days"],
          ],
          value: "check_lag_count",
          count: "check_lag_count",
          countTitle: "Checks",
        }),
        plotHeight: breakdownHeight(5),
      },
    },

    // ── Full width: how customers pay ──
    {
      x: 0,
      y: 40,
      w: 12,
      h: 4,
      component: {
        type: "vega-chart",
        id: "cash-payment-methods",
        title: "How Customers Pay",
        description:
          "Famous cash receipt dollars by deposit type. Famous records ACH and wire under one code, so they share a bar.",
        source: cash({
          kind: "aggregate",
          measures: ["customer_payment_amount", "customer_payment_count"],
          dimensions: ["payment_type"],
        }),
        grammar: "vega-lite",
        spec: breakdownBars({
          category: "payment_type",
          categories: [
            ["check", "Check"],
            ["electronic", "Electronic (ACH/wire)"],
            ["cash", "Cash"],
            ["other", "No deposit type in Famous"],
          ],
          value: "customer_payment_amount",
          count: "customer_payment_count",
          amount: "customer_payment_amount",
          countTitle: "Receipts",
        }),
        plotHeight: breakdownHeight(4),
      },
    },
    {
      x: 0,
      y: 44,
      w: 12,
      h: 11,
      component: {
        type: "table",
        id: "cash-check-customers",
        title: "Customers still paying by check, most check dollars first",
        source: cash({
          kind: "list",
          list: "check_paying_customers",
          limit: 50,
        }),
        previewRows: 10,
        columns: [
          {
            label: "Customer",
            key: "customerName",
            href: "/accounts-receivable/collections/{customerId}",
          },
          {
            label: "Checks",
            key: "check.count",
            align: "right",
          },
          {
            label: "Check $",
            key: "check.amount",
            align: "right",
          },
          {
            label: "Check Share",
            key: "checkSharePct",
            format: "percent100",
            align: "right",
          },
          {
            label: "Last Check",
            key: "lastCheckDate",
            format: "day",
            align: "right",
          },
        ],
        emptyText: "No checks in this range.",
        notes: [
          {
            text: "The {rows} customers with the most check dollars, of {total}.",
            when: { value: "{total - rows}", is: "positive" },
          },
        ],
      },
    },
  ],
};
